/**
 * Formatação no padrão francês para o módulo fiscal.
 *
 *   - Moedas: separador de milhar = '.', sem decimais (43.134 €)
 *   - Percentual: separador decimal = ',', 2 casas (8,49%)
 *   - Inteiros: separador de milhar = '.' (12.345)
 *
 * Aceita números ou strings numéricas (numeric(18,4) vem como string do Postgres
 * via drizzle).
 */

export function toNumber(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

const moneyIntl = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const moneyIntl2 = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const pctIntl = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const intIntl = new Intl.NumberFormat("fr-FR", {
  maximumFractionDigits: 0,
});

export function formatMoney(
  v: number | string | null | undefined,
  opts?: { decimals?: 0 | 2; suffix?: string },
): string {
  const n = toNumber(v);
  if (n === null) return "—";
  const fmt = opts?.decimals === 2 ? moneyIntl2 : moneyIntl;
  return `${fmt.format(n)} ${opts?.suffix ?? "€"}`;
}

/**
 * Aceita razões (0..1) OU pontos percentuais (0..100). Se |v| > 1, assume
 * que já está em pontos percentuais; se |v| <= 1, multiplica por 100.
 */
export function formatPct(
  v: number | string | null | undefined,
  opts?: { alreadyPct?: boolean },
): string {
  const n = toNumber(v);
  if (n === null) return "—";
  const pct = opts?.alreadyPct ? n : Math.abs(n) <= 1 ? n * 100 : n;
  return `${pctIntl.format(pct)}%`;
}

export function formatInt(v: number | string | null | undefined): string {
  const n = toNumber(v);
  if (n === null) return "—";
  return intIntl.format(n);
}

export function formatDays(v: number | string | null | undefined): string {
  const n = toNumber(v);
  if (n === null) return "—";
  if (n === 0) return "0 j";
  return `${intIntl.format(Math.round(n))} j`;
}

/** Cor "positivo verde / negativo vermelho" para valores monetários comparativos. */
export function deltaColorClass(v: number | string | null | undefined): string {
  const n = toNumber(v);
  if (n === null || n === 0) return "text-neutral-400";
  return n > 0 ? "text-lime" : "text-red-400";
}

/** Sinal explícito para variações: "+5,00%" / "−3,21%". */
export function formatPctSigned(
  v: number | string | null | undefined,
  opts?: { alreadyPct?: boolean },
): string {
  const n = toNumber(v);
  if (n === null) return "—";
  const pct = opts?.alreadyPct ? n : Math.abs(n) <= 1 ? n * 100 : n;
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "";
  const abs = Math.abs(pct);
  return `${sign}${pctIntl.format(abs)}%`;
}

export function formatMoneySigned(
  v: number | string | null | undefined,
  opts?: { decimals?: 0 | 2; suffix?: string },
): string {
  const n = toNumber(v);
  if (n === null) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  const fmt = opts?.decimals === 2 ? moneyIntl2 : moneyIntl;
  return `${sign}${fmt.format(Math.abs(n))} ${opts?.suffix ?? "€"}`;
}
